import { LinearFilter, LinearMipmapLinearFilter, Texture } from 'three'
import { isIosWebKit } from '../../viewport/lightweightViewer'
import { isLightweightViewer, readViewportCapabilities } from '../../viewport/readCapabilities'

/**
 * Finish / albedo maps.
 * Desktop keeps catalog resolution (cap 4k so 8k maps cannot blow VRAM).
 * Compact/touch viewers downscale further to stay inside mobile GPU budgets.
 */
export const GPU_TEXTURE_MAX_PX_DESKTOP = 4096
export const GPU_TEXTURE_MAX_PX_MOBILE = 512
export const GPU_TEXTURE_MAX_PX_ANDROID = 768
export const GPU_TEXTURE_MAX_PX_ANDROID_TABLET = 1024
const DESKTOP_FINISH_ANISOTROPY = 16

export function getMaxGpuTexturePx(): number {
  if (!isLightweightViewer()) return GPU_TEXTURE_MAX_PX_DESKTOP
  if (isIosWebKit()) return GPU_TEXTURE_MAX_PX_MOBILE
  return readViewportCapabilities().isCompactTablet
    ? GPU_TEXTURE_MAX_PX_ANDROID_TABLET
    : GPU_TEXTURE_MAX_PX_ANDROID
}

export function cheapenFinishTexture(texture: Texture) {
  if (!isLightweightViewer()) {
    texture.generateMipmaps = true
    texture.minFilter = LinearMipmapLinearFilter
    texture.magFilter = LinearFilter
    texture.anisotropy = Math.max(texture.anisotropy || 1, DESKTOP_FINISH_ANISOTROPY)
    return
  }
  if (isIosWebKit()) {
    texture.anisotropy = 1
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
    texture.magFilter = LinearFilter
    return
  }
  texture.generateMipmaps = true
  texture.minFilter = LinearMipmapLinearFilter
  texture.magFilter = LinearFilter
  texture.anisotropy = 2
}

function readImageSize(img: { width?: number; height?: number; naturalWidth?: number; naturalHeight?: number; videoWidth?: number; videoHeight?: number } | null | undefined) {
  const w = Number(img?.naturalWidth || img?.videoWidth || img?.width || 0)
  const h = Number(img?.naturalHeight || img?.videoHeight || img?.height || 0)
  return { w, h }
}

function nextPowerOfTwo(n: number) {
  return 2 ** Math.ceil(Math.log2(Math.max(1, n)))
}

function sizeWithinBudget(w: number, h: number, maxPx: number) {
  const longest = Math.max(w, h)
  if (!longest || longest <= maxPx) {
    return { width: w, height: h, scale: 1 }
  }
  const scale = maxPx / longest
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
    scale,
  }
}

function sizeForGpuUpload(w: number, h: number, maxPx: number, forcePot: boolean) {
  const fitted = sizeWithinBudget(w, h, maxPx)
  if (!forcePot) return fitted
  return {
    width: Math.min(maxPx, nextPowerOfTwo(fitted.width)),
    height: Math.min(maxPx, nextPowerOfTwo(fitted.height)),
    scale: fitted.scale,
  }
}

function canvasDownscale(img: CanvasImageSource, width: number, height: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, width, height)
  return canvas
}

export function closeGpuImageSource(image: unknown) {
  if (image && typeof (image as ImageBitmap).close === 'function') {
    try {
      (image as ImageBitmap).close()
    } catch {
      // already closed
    }
  }
}

export function closeTextureGpuImage(texture: Texture | null | undefined) {
  if (!texture) return
  closeGpuImageSource(texture.image)
}

/** False → texSubImage2D throws "Overload resolution failed". */
export function isUploadableTextureImage(image: unknown): boolean {
  if (image == null) return false
  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
    return image.width > 0 && image.height > 0
  }
  if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement) {
    return image.complete && image.naturalWidth > 0 && image.naturalHeight > 0
  }
  if (typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement) {
    return image.width > 0 && image.height > 0
  }
  if (typeof OffscreenCanvas !== 'undefined' && image instanceof OffscreenCanvas) {
    return image.width > 0 && image.height > 0
  }
  if (typeof HTMLVideoElement !== 'undefined' && image instanceof HTMLVideoElement) {
    return image.videoWidth > 0 && image.videoHeight > 0
  }
  if (ArrayBuffer.isView(image) || image instanceof ArrayBuffer) return true
  if (Array.isArray(image)) return image.some((item) => isUploadableTextureImage(item))
  return false
}

/**
 * Decode-size → GPU-size. Never returns the original 4k bitmap if it exceeds the budget.
 * createImageBitmap can ignore resize on some Safari builds; we verify dimensions.
 */
export async function downscaleImageForGpu(
  img: CanvasImageSource,
): Promise<{ image: TexImageSource; clampedFrom?: string }> {
  const { w, h } = readImageSize(img as HTMLImageElement)
  const maxPx = getMaxGpuTexturePx()
  const next = sizeForGpuUpload(w, h, maxPx, isLightweightViewer())
  if (next.width === w && next.height === h) return { image: img as TexImageSource }

  try {
    const canvas = canvasDownscale(img, next.width, next.height)
    if (!canvas) return { image: img as TexImageSource }
    return { image: canvas, clampedFrom: `${w}x${h}` }
  } catch {
    return { image: img as TexImageSource }
  }
}

function applyBudgetFlags(texture: Texture, clampedFrom?: string) {
  cheapenFinishTexture(texture)
  texture.userData.__gpuSizeClamped = true
  if (clampedFrom) texture.userData.__gpuClampFrom = clampedFrom
  texture.needsUpdate = true
}

/**
 * Sync fallback when a Texture already has an image (legacy TextureLoader onLoad).
 * Replaces the bitmap in the same turn so the first GPU upload is the small one.
 */
export function clampTextureImageToGpuBudget(texture: Texture | null | undefined): Texture | null | undefined {
  if (!texture) return texture
  if (texture.userData?.__gpuSizeClamped) {
    cheapenFinishTexture(texture)
    return texture
  }

  const img = texture.image as HTMLImageElement | undefined
  const { w, h } = readImageSize(img)
  if (!w || !h) {
    cheapenFinishTexture(texture)
    return texture
  }

  const next = sizeForGpuUpload(w, h, getMaxGpuTexturePx(), isLightweightViewer())
  if (next.width === w && next.height === h) {
    applyBudgetFlags(texture)
    return texture
  }

  try {
    const canvas = canvasDownscale(img as CanvasImageSource, next.width, next.height)
    if (!canvas) {
      applyBudgetFlags(texture)
      return texture
    }
    texture.image = canvas
    applyBudgetFlags(texture, `${w}x${h}`)
  } catch {
    applyBudgetFlags(texture)
  }

  return texture
}
